/**
 * A drop-in replacement for `@fluentui/react-component-ref`, aliased over the
 * real package by the admin and booth Vite configs.
 *
 * semantic-ui-react resolves the DOM node behind a child through that package.
 * Its `Ref` only clones a ref into the child when the child is a `forwardRef`
 * component, and reaches for `ReactDOM.findDOMNode()` for everything else —
 * including plain host elements, which is what `Dropdown`, `Portal`, `Input`,
 * `Sidebar` and friends render. React 18.3 logs a deprecation warning on the
 * first `findDOMNode()` of a page load, and React 19 drops the API.
 *
 * The one change here: host elements ('div', 'i', 'a', …) take the cloned-ref
 * path too, since their ref already *is* the DOM node. That covers everything
 * these apps render on load. What is left on `findDOMNode()` are children that
 * genuinely cannot hold a DOM ref — a function component (`ModalDimmer`, so an
 * open `Modal` still trips the warning once) or a class component (a semantic
 * `<Icon>`/`<Button>` passed as a `Popup` trigger). Those live inside
 * semantic-ui-react and only a fork or a different toolkit would move them.
 *
 * Everything else is the upstream implementation, minus a dev-only string-ref
 * guard that needed `process.env`.
 *
 * Note: this file is bundled into Vite's dependency pre-bundle, so editing it
 * needs `rm -rf apps/<app>/node_modules/.vite` before the change is picked up.
 */
import * as React from 'react';
import * as ReactDOM from 'react-dom';

type InnerRef<T> = React.Ref<T> | undefined;

interface RefChildProps<T> {
  children: React.ReactElement;
  innerRef: InnerRef<T>;
}

/** The function that correctly handles passing refs. */
export const handleRef = <T,>(ref: InnerRef<T>, node: T | null): void => {
  if (typeof ref === 'function') {
    ref(node);
    return;
  }

  if (ref !== null && typeof ref === 'object') {
    // `current` is typed readonly, but a ref object is mutable by design.
    (ref as React.MutableRefObject<T | null>).current = node;
  }
};

/** Checks that the passed object is a valid React ref object. */
export const isRefObject = (ref: unknown): boolean =>
  ref !== null && typeof ref === 'object' && Object.prototype.hasOwnProperty.call(ref, 'current');

/** Passes the child's own DOM node on by cloning a ref into it. */
export class RefForward<T> extends React.Component<RefChildProps<T>> {
  private currentNode: T | null = null;

  private handleRefOverride = (node: T | null) => {
    const { children, innerRef } = this.props;

    handleRef((children as unknown as { ref: InnerRef<T> }).ref, node);
    handleRef(innerRef, node);
    this.currentNode = node;
  };

  override componentDidUpdate(prevProps: RefChildProps<T>) {
    if (prevProps.innerRef !== this.props.innerRef) {
      handleRef(this.props.innerRef, this.currentNode);
    }
  }

  override componentWillUnmount() {
    this.currentNode = null;
  }

  override render() {
    return React.cloneElement(this.props.children, { ref: this.handleRefOverride });
  }
}

/** Last resort for children that cannot hold a DOM ref themselves. */
export class RefFindNode<T> extends React.Component<RefChildProps<T>> {
  private prevNode: T | null = null;

  override componentDidMount() {
    this.prevNode = ReactDOM.findDOMNode(this) as T | null;
    handleRef(this.props.innerRef, this.prevNode);
  }

  override componentDidUpdate(prevProps: RefChildProps<T>) {
    const currentNode = ReactDOM.findDOMNode(this) as T | null;

    if (this.prevNode !== currentNode) {
      this.prevNode = currentNode;
      handleRef(this.props.innerRef, currentNode);
    }

    if (prevProps.innerRef !== this.props.innerRef) {
      handleRef(this.props.innerRef, currentNode);
    }
  }

  override componentWillUnmount() {
    handleRef(this.props.innerRef, null);
    this.prevNode = null;
  }

  override render() {
    return this.props.children;
  }
}

const REACT_FORWARD_REF_TYPE = Symbol.for('react.forward_ref');

/** Whether a ref put on this element would come back as a DOM node. */
function holdsDomRef(child: React.ReactElement): boolean {
  if (typeof child.type === 'string') {
    return true;
  }

  const type = child.type as { $$typeof?: symbol } | null;
  return typeof type === 'object' && type !== null && type.$$typeof === REACT_FORWARD_REF_TYPE;
}

export const Ref = <T,>(props: RefChildProps<T> & Record<string, unknown>): React.ReactElement => {
  const { children, innerRef, ...rest } = props;
  const child = React.Children.only(children);
  const ElementType = holdsDomRef(child) ? RefForward : RefFindNode;
  const childWithProps = Object.keys(rest).length > 0 ? React.cloneElement(child, rest) : child;

  return React.createElement(ElementType as React.ComponentClass<RefChildProps<T>>, {
    innerRef,
    children: childWithProps
  });
};
